import Big from 'big.js';
import dayjs from 'dayjs';
import { FormikProvider, useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as yup from 'yup';
import CurrencyInput from '@/modules/common/components/CurrencyInput.tsx';
import {
  RegisterBanksSelect,
  mapTransactionTypeToBankType,
} from '@/modules/common/components/RegisterBanksSelect.tsx';
import UploadDocumentsBlock from '@/modules/common/components/UploadDocumentsBlock.tsx';
import { BigRoundMode } from '@/modules/common/constants/big-round-mode.ts';
import { useQuantityPricePerShareTotal } from '@/modules/common/hooks/useQuantityPricePerShareTotal.ts';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { useRegisterQuery } from '@/modules/common/queries/registers.ts';
import { preventFormDefault } from '@/modules/common/utils/preventFormDefault.ts';
import {
  useCurrentUserRegistersQuery,
  useRegisterClassListInfinityQuery,
} from '@/modules/investor-dashboard/queries/registers.tsx';
import { useRegisterShareholderList } from '@/modules/investor-dashboard/queries/shareholders.tsx';
import { useCreateTransactionMutation } from '@/modules/investor-dashboard/queries/transactions.tsx';
import FractionalShares from '@/modules/issuer-dashboard/components/FractionalShares.tsx';
import {
  PaymentDividendOption,
  PaymentDividendSelect,
} from '@/modules/issuer-dashboard/components/PaymentDividendSelect.tsx';
import {
  Button,
  CalendarInput,
  CalendarInputProps,
  CustomCheckbox,
  ModalContentWrapper,
  Select,
  SelectButtons,
  SelectOption,
  SelectPaginate,
  SelectPaginateOption,
  TextInputField,
  getSelectOptionByEnum,
} from 'ui';
import {
  CreateTransactionRequest,
  RegisterBankExtended,
  RoundTo,
  RoundingType,
  ShareholderExtendResponse,
  TransactionStatus,
  TransactionType,
} from 'api';

const validationSchema = yup.object({
  shareholder_id: yup
    .number()
    .typeError('Shareholder id must be a number')
    .required('This field is required')
    .moreThan(0, 'This field is required'),
  document_data: yup.array().of(yup.string().trim()),
  register_id: yup
    .number()
    .typeError('Register id must be a number')
    .required('This field is required')
    .moreThan(0, 'This field is required'),
  register_class_id: yup
    .number()
    .typeError('Security class id must be a number')
    .required('This field is required')
    .moreThan(0, 'This field is required'),
  quantity: yup
    .number()
    .typeError('Quantity must be a number')
    .moreThan(0, 'Quantity must be more than 0')
    .required('This field is required'),
  price_per_share: yup
    .number()
    .typeError('Price must be a number')
    .required('This field is required'),
  memo: yup.string().trim(),
  total: yup.number().typeError('Total must be a number').required('This field is required'),
  dividend_reinvestment: yup.boolean().when(['type'], ([type], schema) => {
    return type === TransactionType.Subscription || type === TransactionType.OpeningBalance
      ? schema.required('This select is required')
      : schema.nullable();
  }),
  dividend_event_id: yup.number().when(['type'], ([type], schema) => {
    return type === TransactionType.Drp
      ? schema.moreThan(0, 'This field is required').required('This field is required')
      : schema.nullable();
  }),
  status: yup
    .mixed<TransactionStatus>()
    .oneOf(Object.values(TransactionStatus))
    .required('This select is required'),
  issue_date: yup
    .string()
    .trim()
    .when('status', ([status], schema) => {
      if (status === TransactionStatus.Completed) {
        return schema.required('This field is required');
      }
      return schema.nullable();
    }),
  payment_issue_date: yup
    .string()
    .trim()
    .when(['status', 'type'], ([status, type], schema) => {
      if (status === TransactionStatus.Completed && type !== TransactionType.Drp) {
        return schema.required('This field is required');
      }
      return schema.nullable();
    }),
  to_bank_id: yup.number().when(['status'], ([status], schema) => {
    return status === TransactionStatus.Completed
      ? schema.moreThan(0, 'This field is required').required('This field is required')
      : schema.nullable();
  }),
  round_to: yup.mixed<RoundTo>().when(['status'], ([status], schema) => {
    return status === TransactionStatus.Completed
      ? schema.oneOf(Object.values(RoundTo)).required('This select is required')
      : schema.nullable();
  }),
  do_not_notify_investor: yup.boolean().required('This select is required'),
});

const initialValues: CreateTransactionRequest & { register_id: number } = {
  register_class_id: 0,
  shareholder_id: 0,
  type: TransactionType.Subscription,
  quantity: 1,
  price_per_share: 1,
  memo: '',
  dividend_reinvestment: null as unknown as boolean,
  dividend_event_id: undefined,
  total: 1,
  register_id: 0,
  document_data: [],
  status: TransactionStatus.Pending,
  issue_date: undefined,
  payment_issue_date: undefined,
  to_bank_id: undefined,
  do_not_notify_investor: false,
  round_to: undefined,
};

export const dividendReinvestmentOptions = [
  { label: 'Reinvest in the Fund', value: true },
  { label: 'Receive cash distributions', value: false },
];

interface RegisterClassSelectPaginate {
  id: number;
  price_per_share: number;
}

export interface IssuerAddSubscriptionModalProps {
  register_id: number;
  open: boolean;
  mode: TransactionType;
  onClose: () => void;
  shareholderId?: number;
}

export const IssuerAddSubscriptionModal = ({
  open,
  mode,
  register_id,
  onClose,
  shareholderId,
}: IssuerAddSubscriptionModalProps) => {
  const getRegisterTypeStrings = useRegisterTypeStrings();

  const { data: registerData } = useRegisterQuery(register_id, !register_id);
  const [roundingType, setRoundingType] = useState<RoundingType>();
  const [selectedDividend, setSelectedDividend] = useState<PaymentDividendOption | null>(null);

  const { rounding_type, allows_fractional_shares = true } = useMemo(() => {
    const { rounding_type, allows_fractional_shares } = registerData?.data ?? {};
    return { rounding_type, allows_fractional_shares };
  }, [registerData]);

  const formik = useFormik({
    initialValues: {
      ...initialValues,
      round_to: rounding_type === RoundingType.Up ? RoundTo.Up : RoundTo.Down,
      register_id,
      type: mode,
      shareholder_id: shareholderId ?? initialValues.shareholder_id,
    },
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      await createSubscription(values);
      resetForm();
      setSelectedDividend(null);
      onClose();
    },
    enableReinitialize: true,
  });

  useEffect(() => {
    setRoundingType(rounding_type);
    formik.setFieldValue('round_to', rounding_type === RoundingType.Up ? RoundTo.Up : RoundTo.Down);
  }, [rounding_type]);

  const { onChangeTotal, onChangeQuantity, onChangePricePerShare } = useQuantityPricePerShareTotal({
    formik,
    allowsFractionalSharesQuantity: allows_fractional_shares,
    roundingTypeQuantity: roundingType,
  });

  const { mutateAsync: createSubscription } = useCreateTransactionMutation({
    setErrors: formik.setErrors,
  });

  const { data: registersResponse, isLoading: isRegistersLoading } = useCurrentUserRegistersQuery();
  const { fetchNextPage: fetchNextRegisterClassListPage } = useRegisterClassListInfinityQuery(
    { page: 1, page_size: 10, register_id: formik.values.register_id },
    true,
  );
  const { data: registerShareholderList, isLoading: isRegisterShareholderListLoading } =
    useRegisterShareholderList({ register_id: formik.values.register_id });

  const registerOptions = useMemo<SelectOption<number>[]>(() => {
    if (!registersResponse?.data) return [];

    return registersResponse.data?.map(item => ({
      label: item.register_obj?.name ?? '',
      value: item.register_id,
    }));
  }, [registersResponse]);

  const registerShareholderListOptions = useMemo<SelectOption<ShareholderExtendResponse>[]>(() => {
    if (!registerShareholderList?.shareholders) return [];

    return registerShareholderList?.shareholders.map(item => ({
      label: item.entity.entity_name,
      value: { ...item, toString: () => item.id },
    }));
  }, [registerShareholderList]);

  const handleAsyncRegisterClassList = async (currentPage: number, search?: string) => {
    const response = await fetchNextRegisterClassListPage({
      pageParam: {
        page: currentPage,
        search,
      },
    });

    const options: SelectPaginateOption<RegisterClassSelectPaginate>[] =
      response.data?.pages.reduce((acc: any, { data, page }) => {
        if (page !== currentPage) {
          return acc;
        }

        return data.map(({ class_name, id, current_price_per_share }) => ({
          label: class_name,
          value: { id, price_per_share: current_price_per_share, toString: () => class_name },
        }));
      }, []) || [];
    return options;
  };

  const handleChangeRegister = (registerId: number | null) => {
    formik.setFieldValue('register_id', registerId);
    formik.setFieldValue('register_class_id', initialValues.register_class_id);
    formik.setFieldValue('shareholder_id', initialValues.shareholder_id);
    formik.setFieldValue('dividend_event_id', undefined);
    formik.setFieldError('dividend_event_id', '');
    setSelectedDividend(null);

    formik.setFieldValue('quantity', initialValues.quantity);
  };

  const handleChangeShareholder = useCallback(
    (shareholder: ShareholderExtendResponse | null) => {
      formik.setFieldValue('shareholder_id', shareholder?.id);
      formik.setFieldError('shareholder_id', '');

      const reinvestmentElections = shareholder?.dividend_reinvestment_extended;
      if (formik.values.register_class_id && reinvestmentElections) {
        const currentReinvestmentElection =
          reinvestmentElections.find(item => item.id === formik.values.register_class_id)
            ?.is_reinvest ?? null;
        formik.setFieldValue('dividend_reinvestment', currentReinvestmentElection);
        formik.setFieldError('dividend_reinvestment', '');
      }
    },
    [formik.values.register_class_id],
  );

  const handleChangeRegisterClass = useCallback(
    (register_class_id: number, price_per_share: number) => {
      formik.setFieldValue('register_class_id', register_class_id);
      formik.setFieldValue('price_per_share', price_per_share);
      formik.setFieldValue('quantity', initialValues.quantity);

      setTimeout(() => {
        onChangePricePerShare(price_per_share);
      }, 1);

      formik.setFieldError('register_class_id', '');
      formik.setFieldError('price_per_share', '');
      formik.setFieldError('quantity', '');
      formik.setFieldError('total', '');

      if (formik.values.shareholder_id && registerShareholderListOptions && register_class_id) {
        const reinvestmentElections = registerShareholderListOptions?.find(
          item => item.value.id === formik.values.shareholder_id,
        )?.value?.dividend_reinvestment_extended;
        const currentReinvestmentElection =
          reinvestmentElections?.find(item => item.id === register_class_id)?.is_reinvest ?? null;
        formik.setFieldValue('dividend_reinvestment', currentReinvestmentElection);
        formik.setFieldError('dividend_reinvestment', '');
      }
    },
    [formik.values.shareholder_id, registerShareholderListOptions],
  );

  const handleChangeDocuments = useCallback((filesData: string[]) => {
    formik.setFieldValue('document_data', filesData);
    formik.setFieldError('document_data', '');
  }, []);

  const handleChangeReinvestment = (reinvestment: boolean) => {
    formik.setFieldValue('dividend_reinvestment', reinvestment);
    formik.setFieldError('dividend_reinvestment', '');
  };

  const handleChangeSelect =
    (field: keyof CreateTransactionRequest) => (option: string | boolean | null | any) => {
      formik.setFieldValue(field, option);
      formik.setFieldError(field, '');
    };

  const handleChangeDate =
    (fieldName: keyof CreateTransactionRequest): CalendarInputProps['onChange'] =>
    date => {
      if (!date) {
        formik.setFieldValue(fieldName, '');
        formik.setFieldError(fieldName, '');
        return;
      }
      const currentDate = date.utc().format('YYYY-MM-DDTHH:mm:ss.SSSSSS[+00:00]');
      formik.setFieldValue(fieldName, currentDate);
      formik.setFieldError(fieldName, '');
    };

  const handleChangeBank =
    (fieldName: keyof CreateTransactionRequest) =>
    (item: SelectPaginateOption<RegisterBankExtended> | null) => {
      formik.setFieldValue(fieldName, item?.value.id);
      formik.setFieldError(fieldName, '');
    };

  const handleChangeDividend = useCallback((option: PaymentDividendOption | null) => {
    setSelectedDividend(option);
    formik.setFieldValue('dividend_event_id', option?.value.id);
    formik.setFieldError('dividend_event_id', '');
  }, []);

  const amountExcess = useMemo(
    () =>
      formik?.values?.total && formik.values.price_per_share
        ? Number(new Big(formik?.values?.total || 0).mod(formik.values.price_per_share).valueOf())
        : 0,
    [formik?.values?.total, formik.values.price_per_share],
  );

  const isAmountFractional = amountExcess !== 0;

  const roundDownShares = useMemo(
    () =>
      formik?.values?.total && formik.values.price_per_share
        ? Number(
            new Big(formik.values.total)
              .div(formik.values.price_per_share)
              .round(0, BigRoundMode.roundDown)
              .valueOf(),
          )
        : 0,
    [formik.values.total, formik.values.price_per_share],
  );

  const roundUpShares = useMemo(
    () =>
      formik?.values?.total && formik.values.price_per_share
        ? Number(
            new Big(formik.values.total)
              .div(formik.values.price_per_share)
              .round(0, BigRoundMode.roundUp)
              .valueOf(),
          )
        : 0,
    [formik.values.total, formik.values.price_per_share],
  );

  useEffect(() => {
    console.log('Errors:', formik.errors);
  }, [formik.errors]);

  return (
    <ModalContentWrapper
      open={open}
      title={
        mode === TransactionType.Subscription
          ? `Add ${getRegisterTypeStrings('application', {
              registerId: formik.values.register_id,
            })}`
          : mode === TransactionType.Drp
          ? `New ${getRegisterTypeStrings('dividend', {
              registerId: formik.values.register_id,
            })} reinvestment`
          : 'New redemption'
      }
      onClose={onClose}
    >
      <FormikProvider value={formik}>
        <form className="w-full flex flex-col" onSubmit={preventFormDefault}>
          <div className="w-full flex flex-col gap-8 pb-2">
            <div className="w-full flex items-start gap-6">
              <Select
                label="REGISTER"
                options={registerOptions}
                value={formik.values.register_id}
                isLoading={isRegistersLoading}
                onChange={handleChangeRegister}
                error={formik.errors.register_id}
              />
              <SelectPaginate<RegisterClassSelectPaginate>
                key={formik.values.register_id}
                onAsync={handleAsyncRegisterClassList}
                label="CLASS"
                onChange={({ value: { id, price_per_share } }) =>
                  handleChangeRegisterClass(id, price_per_share)
                }
                error={formik.errors.register_class_id}
              />
            </div>
            <Select<ShareholderExtendResponse>
              label="SHAREHOLDER"
              options={registerShareholderListOptions}
              isLoading={isRegisterShareholderListLoading}
              value={{ id: formik.values.shareholder_id } as ShareholderExtendResponse}
              onChange={handleChangeShareholder}
              error={formik.errors.shareholder_id}
            />
            {mode === TransactionType.Drp && (
              <PaymentDividendSelect
                value={selectedDividend}
                onChange={handleChangeDividend}
                error={formik.errors.dividend_event_id}
                label={`Select ${getRegisterTypeStrings('dividend', { capitalize: true })}`}
                registerId={formik.values.register_id}
              />
            )}
            <CurrencyInput
              currency=""
              name="quantity"
              label="quantity of shares"
              value={formik.values.quantity}
              allowFractional={allows_fractional_shares}
              roundingType={roundingType}
              onChange={onChangeQuantity}
            />
            <CurrencyInput
              name="total"
              label="total"
              error={formik.errors.total}
              value={formik.values.total}
              onChange={onChangeTotal}
            />
            <CurrencyInput
              name="price_per_share"
              label={`price per share/unit (${getRegisterTypeStrings('nav', {
                registerId: formik.values.register_id,
              })})`}
              decimalPoints={4}
              value={formik.values.price_per_share}
              error={formik.errors.price_per_share}
              onChange={onChangePricePerShare}
            />

            {!allows_fractional_shares &&
              isAmountFractional &&
              formik.values.status === TransactionStatus.Completed && (
                <FractionalShares
                  amount={formik.values.total || 0}
                  price_per_share={formik.values.price_per_share}
                  amountExcess={amountExcess}
                  value={formik.values.round_to}
                  roundDownShares={roundDownShares}
                  roundUpShares={roundUpShares}
                  onChange={value => {
                    setRoundingType(value === RoundTo.Up ? RoundingType.Up : RoundingType.Down);
                    formik.setFieldValue('round_to', value);
                    // if (value === RoundTo.Up) {
                    //   onChangeQuantity(roundUpShares);
                    // } else {
                    //   onChangeQuantity(roundDownShares);
                    // }
                  }}
                  inFuture={true}
                />
              )}

            <Select
              value={formik.values.status}
              label="Status"
              placeholder="Select transaction status"
              onChange={value => {
                handleChangeSelect('status')(value);
                if (value === TransactionStatus.Pending) {
                  handleChangeSelect('to_bank_id')(undefined);
                  handleChangeSelect('issue_date')(undefined);
                  handleChangeSelect('payment_issue_date')(undefined);
                }
              }}
              options={getSelectOptionByEnum<TransactionStatus>(TransactionStatus).filter(
                item =>
                  item.value === TransactionStatus.Pending ||
                  item.value === TransactionStatus.Completed,
              )}
              error={formik.errors.status}
            />

            {formik.values.status === TransactionStatus.Completed && (
              <RegisterBanksSelect
                onChange={handleChangeBank('to_bank_id')}
                value={formik.values.to_bank_id}
                label="To Bank"
                key={`To_Bank_direction_${formik.values.type}`}
                clearable={true}
                error={formik.errors.to_bank_id}
                needBankType={mapTransactionTypeToBankType[formik.values.type]}
                prefillBank={true}
                is_overall={true}
              />
            )}

            {formik.values.status === TransactionStatus.Completed && (
              <CalendarInput
                label="Units issue date"
                name="issue_date"
                onChange={handleChangeDate('issue_date')}
                error={formik.errors.issue_date}
                format="DD-MM-YYYY HH:mm"
                value={formik.values.issue_date ? dayjs(formik.values.issue_date) : undefined}
                showTime={true}
              />
            )}

            {formik.values.status === TransactionStatus.Completed &&
              mode !== TransactionType.Drp && (
                <CalendarInput
                  label="Payment date"
                  name="payment_issue_date"
                  onChange={handleChangeDate('payment_issue_date')}
                  error={formik.errors.payment_issue_date}
                  format="DD-MM-YYYY HH:mm"
                  value={
                    formik.values.payment_issue_date
                      ? dayjs(formik.values.payment_issue_date)
                      : undefined
                  }
                  showTime={true}
                />
              )}

            <CustomCheckbox
              label="Do not notify investor"
              checked={formik.values.do_not_notify_investor}
              onChange={e => handleChangeSelect('do_not_notify_investor')(e.target.checked)}
            />

            {mode === TransactionType.Subscription && (
              <SelectButtons<boolean>
                label="REINVESTMENT DISTRIBUTION ELECTION"
                options={dividendReinvestmentOptions}
                selected={dividendReinvestmentOptions.find(
                  ({ value }) => value === formik.values.dividend_reinvestment,
                )}
                onChange={handleChangeReinvestment}
                className="mt-6"
                error={formik.errors.dividend_reinvestment}
              />
            )}

            <UploadDocumentsBlock
              label="Upload documents"
              classes={{ label: 'text-base font-medium' }}
              error={formik.errors.document_data}
              onChange={handleChangeDocuments}
              value={formik.values.document_data || []}
            />
            <TextInputField
              name="memo"
              label="Memo (Optional)"
              placeholder="Memo..."
              className="resize-none"
              textarea={true}
            />
          </div>
          <Button onClick={formik.submitForm} loading={formik.isSubmitting}>
            Confirm
          </Button>
        </form>
      </FormikProvider>
    </ModalContentWrapper>
  );
};
